import { useState, useEffect, useCallback } from 'react';
import {
  getButtonFunctions,
  createButtonFunction,
  updateButtonFunction,
  deleteButtonFunction,
  ButtonFunction,
  ButtonFunctionInput,
} from '../../services/admin';

const emptyForm: ButtonFunctionInput = {
  name: '',
  description: '',
  sort_order: 0,
};

/** Global "Button Functions" catalog (Settings > Model Catalog > "Button Functions") - a
 * reference list of logical hardware-button function names, independent of any specific device
 * model. Seeded server-side with an initial set (PTT, SOS, F1-F3, Group Selector up/down) - admins
 * can add more here. */
export default function ButtonFunctionsManager() {
  const [functions, setFunctions] = useState<ButtonFunction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ButtonFunctionInput>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      setFunctions(await getButtonFunctions());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load button functions');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      if (!showForm) load(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [load, showForm]);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sort_order: functions.length });
    setShowForm(true);
  };

  const openEdit = (f: ButtonFunction) => {
    setEditingId(f.id);
    setForm({ name: f.name, description: f.description ?? '', sort_order: f.sort_order });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload: ButtonFunctionInput = { ...form };
      if (editingId) {
        await updateButtonFunction(editingId, payload);
      } else {
        await createButtonFunction(payload);
      }
      closeForm();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save button function');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (f: ButtonFunction) => {
    if (!confirm(`Delete the "${f.name}" button function?`)) return;
    try {
      await deleteButtonFunction(f.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete button function');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-bold">🔘 Button Functions</h3>
          <p className="text-gray-400 text-sm mt-1">Global catalog of hardware-button function names, shared across every model.</p>
        </div>
        <button
          onClick={openAdd}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          + Add Button Function
        </button>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-600 text-red-300 rounded-lg p-3">{error}</div>
      )}

      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-900/60 text-gray-400">
            <tr>
              <th className="px-4 py-3 text-left w-40">Name</th>
              <th className="px-4 py-3 text-left">Description</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {functions.map((f) => (
              <tr key={f.id} className="hover:bg-gray-700/30">
                <td className="px-4 py-3 font-mono font-semibold align-top">
                  {f.name}
                </td>
                <td className="px-4 py-3 text-gray-400">{f.description || '—'}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(f)} className="text-blue-400 hover:text-blue-300 font-semibold mr-3">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(f)} className="text-red-400 hover:text-red-300 font-semibold">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {functions.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-center text-gray-400">
                  No button functions yet - add one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div data-management-modal className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-md p-6 space-y-4">
            <h3 className="text-2xl font-bold text-white">{editingId ? 'Edit Button Function' : 'Add Button Function'}</h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="block">
                <p className="text-sm font-semibold text-gray-300 mb-1">Name</p>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent font-mono"
                  placeholder="e.g., F4"
                  required
                />
              </label>

              <label className="block">
                <p className="text-sm font-semibold text-gray-300 mb-1">Description</p>
                <textarea
                  value={form.description ?? ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="Optional"
                />
              </label>

              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
